import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";

export default class extends Controller {
  static targets = ["trigger", "template"];

  connect() {
    // Clone from <template> so the article link is never in the live DOM before
    // portaling — avoids invalid nested <a> when this tooltip appears inside a
    // linked table row (e.g. the Administrate collection view).
    this._content = this.templateTarget.content.firstElementChild.cloneNode(true);

    // Portal: append to body so the tooltip escapes overflow/contain constraints
    // and any CSS transform ancestors that would break fixed positioning.
    document.body.appendChild(this._content);

    this.popperInstance = createPopper(this.triggerTarget, this._content, {
      placement: "top",
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } },
        { name: "flip", options: { fallbackPlacements: ["bottom"] } },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 12 } },
      ],
    });

    this.boundShow = this.show.bind(this);
    this.boundHide = this.hide.bind(this);
    this.boundTriggerClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };
    this.boundContentClick = (e) => {
      e.stopPropagation();
    };

    this.triggerTarget.addEventListener("mouseenter", this.boundShow);
    this.triggerTarget.addEventListener("mouseleave", this.boundHide);
    this.triggerTarget.addEventListener("focus", this.boundShow);
    this.triggerTarget.addEventListener("blur", this.boundHide);
    this.triggerTarget.addEventListener("click", this.boundTriggerClick);

    this._content.addEventListener("mouseenter", this.boundShow);
    this._content.addEventListener("mouseleave", this.boundHide);
    this._content.addEventListener("focusin", this.boundShow);
    this._content.addEventListener("focusout", this.boundHide);
    this._content.addEventListener("click", this.boundContentClick);
  }

  disconnect() {
    clearTimeout(this.hideTimeout);

    this.triggerTarget.removeEventListener("mouseenter", this.boundShow);
    this.triggerTarget.removeEventListener("mouseleave", this.boundHide);
    this.triggerTarget.removeEventListener("focus", this.boundShow);
    this.triggerTarget.removeEventListener("blur", this.boundHide);
    this.triggerTarget.removeEventListener("click", this.boundTriggerClick);

    this._content.removeEventListener("mouseenter", this.boundShow);
    this._content.removeEventListener("mouseleave", this.boundHide);
    this._content.removeEventListener("focusin", this.boundShow);
    this._content.removeEventListener("focusout", this.boundHide);
    this._content.removeEventListener("click", this.boundContentClick);

    this.popperInstance?.destroy();

    // The content was cloned from a <template> — just remove it from the body.
    this._content?.remove();
  }

  show() {
    clearTimeout(this.hideTimeout);
    this._content.classList.remove("hidden");
    this.popperInstance.update();
  }

  hide() {
    this.hideTimeout = setTimeout(() => {
      this._content.classList.add("hidden");
    }, 100);
  }
}
